/**
 * UnifiedKiosk - Multi-mode scanning kiosk
 * Features:
 * - Mode selection before scanning
 * - Detailed success/failure screens with specific reasons
 * - Color-coded results (green/red/yellow)
 * - Sound feedback
 * - Auto-reset for continuous scanning
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { getToken } from '@/lib/authToken';
import { 
  Camera, CameraOff, Volume2, VolumeX, X, Maximize,
  CheckCircle, XCircle, AlertTriangle, RefreshCw,
  UserCheck, Utensils, Home, BookOpen, Shield, Crown,
  ArrowLeft, Clock, Building, MapPin, User, Ticket, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Scan mode configurations
const SCAN_MODES = {
  checkin: {
    id: 'checkin',
    name: 'General Event Check-In',
    icon: UserCheck,
    color: 'from-indigo-600 to-indigo-500',
    bgColor: 'bg-indigo-600',
    description: 'Check delegates into the event'
  },
  meal: {
    id: 'meal',
    name: 'Meals',
    icon: Utensils,
    color: 'from-orange-600 to-orange-500',
    bgColor: 'bg-orange-600',
    description: 'Verify meal entitlements'
  },
  accommodation: {
    id: 'accommodation',
    name: 'Accommodation',
    icon: Home,
    color: 'from-blue-600 to-blue-500',
    bgColor: 'bg-blue-600',
    description: 'Verify housing access'
  },
  session: {
    id: 'session',
    name: 'Sessions',
    icon: BookOpen,
    color: 'from-purple-600 to-purple-500',
    bgColor: 'bg-purple-600',
    description: 'Session/workshop access'
  },
  security: {
    id: 'security',
    name: 'Security Access',
    icon: Shield,
    color: 'from-slate-700 to-slate-600',
    bgColor: 'bg-slate-700',
    description: 'Security zone verification'
  },
  vip: {
    id: 'vip',
    name: 'VIP / Lounge Access',
    icon: Crown,
    color: 'from-amber-600 to-amber-500',
    bgColor: 'bg-amber-600',
    description: 'VIP area access'
  }
};

const UnifiedKiosk = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  
  const [event, setEvent] = useState(null);
  const [scanMode, setScanMode] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [cameraList, setCameraList] = useState([]);
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [stats, setStats] = useState({ scanned: 0, approved: 0, denied: 0 });
  const [recentScans, setRecentScans] = useState([]);
  
  // Mode-specific settings
  const [mealType, setMealType] = useState('lunch');
  const [diningZone, setDiningZone] = useState('main');
  const [building, setBuilding] = useState('');
  const [accessPoint, setAccessPoint] = useState('main');
  const [sessionName, setSessionName] = useState('');
  const [accessZone, setAccessZone] = useState('main');
  
  // Scanner states
  const [cameraError, setCameraError] = useState(null);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [manualQrInput, setManualQrInput] = useState('');
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [shouldStartScanner, setShouldStartScanner] = useState(false); // Flag to trigger scanner start after DOM ready
  
  const html5QrCodeRef = useRef(null);
  const scannerContainerId = 'unified-kiosk-scanner';
  const audioContextRef = useRef(null);

  useEffect(() => {
    // Get cameras with better error handling
    initializeCameras();
    fetchEvent();
    
    return () => {
      stopScanner();
    };
  }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect to actually start the scanner after DOM is ready
  useEffect(() => {
    if (shouldStartScanner && scanning) {
      // DOM should now have the scanner container
      const startScannerNow = async () => {
        // Wait a tick for React to finish rendering
        await new Promise(resolve => setTimeout(resolve, 50));
        
        // Check if element exists
        const element = document.getElementById(scannerContainerId);
        if (!element) {
          console.error('Scanner container still not found after waiting');
          setCameraError('Scanner container not ready. Please try again.');
          setCameraLoading(false);
          setShouldStartScanner(false);
          return;
        }
        
        await actuallyStartScanner();
        setShouldStartScanner(false);
      };
      startScannerNow();
    }
  }, [shouldStartScanner, scanning]);

  const initializeCameras = async () => {
    try {
      // Check if mediaDevices is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraError('Camera API not available. Please use a modern browser with HTTPS.');
        return;
      }

      // First request permission
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true });
        // Release the stream immediately - we just needed permission
        stream.getTracks().forEach(track => track.stop());
      } catch (permErr) {
        if (permErr.name === 'NotAllowedError') {
          setCameraError('Camera access denied. Please allow camera access in your browser settings.');
          return;
        }
        if (permErr.name === 'NotFoundError') {
          setCameraError('No camera found on this device.');
          return;
        }
        // Continue anyway - might still work with Html5Qrcode.getCameras()
      }

      const cameras = await Html5Qrcode.getCameras();
      if (!cameras || cameras.length === 0) {
        setCameraError('No cameras found. Please connect a camera and refresh.');
        return;
      }

      setCameraList(cameras);
      setCameraError(null);
      
      // Try to find a back camera (preferred for scanning)
      const backCamera = cameras.find(c => 
        c.label.toLowerCase().includes('back') || 
        c.label.toLowerCase().includes('rear') ||
        c.label.toLowerCase().includes('environment')
      );
      
      // Fallback order: back camera -> first camera
      setSelectedCamera(backCamera?.id || cameras[0]?.id);
    } catch (err) {
      console.error('Camera initialization error:', err);
      setCameraError('Failed to detect cameras. Please check browser permissions.');
    }
  };

  // NOTE: `getToken` is imported from '@/lib/authToken' at the top.
  // A previous refactor left behind `const getToken = () => getToken();`
  // here which shadowed the import and infinite-recursed → RangeError:
  // Maximum call stack size exceeded. That crash was the true cause of
  // the "Connection error" the user reported (the fetch call never made
  // it past the Authorization header). Removed. (iter-438c)

  const fetchEvent = async () => {
    try {
      const res = await fetch(`${API_URL}/api/events/${eventId}`);
      if (res.ok) setEvent(await res.json());
    } catch (e) { console.error(e); }
  };

  const playSound = useCallback((success) => {
    if (!soundEnabled) return;
    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      if (success) {
        // Success: Two ascending tones
        oscillator.frequency.value = 880;
        gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.15);
        
        // Second tone
        setTimeout(() => {
          const osc2 = ctx.createOscillator();
          const gain2 = ctx.createGain();
          osc2.connect(gain2);
          gain2.connect(ctx.destination);
          osc2.frequency.value = 1174.66;
          gain2.gain.setValueAtTime(0.3, ctx.currentTime);
          gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
          osc2.start(ctx.currentTime);
          osc2.stop(ctx.currentTime + 0.2);
        }, 100);
      } else {
        // Error: Low descending tone
        oscillator.frequency.value = 330;
        gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.4);
      }
    } catch (e) {
      console.log('Audio error:', e);
    }
  }, [soundEnabled]);

  const startScanner = async () => {
    // Validate we have cameras
    if (!selectedCamera && cameraList.length === 0) {
      setCameraError('No camera available. Please connect a camera or allow camera access.');
      return;
    }
    
    setCameraError(null);
    setResult(null);
    setCameraLoading(true);
    
    // Make sure any previous scanner is stopped
    await stopScanner();
    
    // Set scanning to true first (this will render the scanner container)
    setScanning(true);
    // Then trigger the actual scanner start via useEffect
    setShouldStartScanner(true);
  };

  const actuallyStartScanner = async () => {
    try {
      html5QrCodeRef.current = new Html5Qrcode(scannerContainerId);
      
      // Try with selected camera first
      const cameraToUse = selectedCamera || cameraList[0]?.id;
      
      if (cameraToUse) {
        try {
          await html5QrCodeRef.current.start(
            cameraToUse,
            { fps: 15, qrbox: { width: 280, height: 280 } },
            handleScanResult,
            () => {}
          );
          setCameraLoading(false);
          setCameraError(null);
          return; // Success!
        } catch (specificCameraErr) {
          console.warn('Selected camera failed, trying fallback:', specificCameraErr);
          // Clean up failed attempt
          try {
            await html5QrCodeRef.current.stop();
            html5QrCodeRef.current.clear();
          } catch (e) { console.warn("QR cleanup:", e?.message); }
        }
      }
      
      // Fallback: Try with facingMode constraints instead of device ID
      try {
        html5QrCodeRef.current = new Html5Qrcode(scannerContainerId);
        await html5QrCodeRef.current.start(
          { facingMode: "environment" }, // Back camera fallback
          { fps: 15, qrbox: { width: 280, height: 280 } },
          handleScanResult,
          () => {}
        );
        setCameraLoading(false);
        setCameraError(null);
        return; // Success with fallback!
      } catch (envErr) {
        console.warn('Environment camera failed, trying any camera:', envErr);
        try {
          await html5QrCodeRef.current.stop();
          html5QrCodeRef.current.clear();
        } catch (e) { console.warn("QR cleanup:", e?.message); }
      }
      
      // Final fallback: Try any camera
      try {
        html5QrCodeRef.current = new Html5Qrcode(scannerContainerId);
        await html5QrCodeRef.current.start(
          { facingMode: "user" }, // Front camera as last resort
          { fps: 15, qrbox: { width: 280, height: 280 } },
          handleScanResult,
          () => {}
        );
        setCameraLoading(false);
        setCameraError(null);
        return; // Success with user camera!
      } catch (userErr) {
        console.warn('User camera also failed:', userErr);
      }
      
      // All fallbacks failed
      throw new Error('All camera options exhausted');
      
    } catch (err) {
      console.error('Scanner start failed:', err);
      setCameraLoading(false);
      
      // Provide specific error messages
      const errorMessage = err.message || err.toString();
      if (errorMessage.includes('NotAllowedError') || errorMessage.includes('Permission denied')) {
        setCameraError('Camera access denied. Please allow camera access in your browser settings and refresh.');
      } else if (errorMessage.includes('NotFoundError') || errorMessage.includes('Requested device not found')) {
        setCameraError('The selected camera is unavailable. Please try a different camera or refresh.');
      } else if (errorMessage.includes('NotReadableError') || errorMessage.includes('Could not start')) {
        setCameraError('Camera is already in use by another application. Please close other apps using the camera.');
      } else if (errorMessage.includes('OverconstrainedError')) {
        setCameraError('Camera does not support required settings. Try a different camera.');
      } else {
        setCameraError(`Camera could not start: ${errorMessage}. Try the manual entry option.`);
      }
    }
  };

  const stopScanner = async () => {
    setShouldStartScanner(false);
    if (html5QrCodeRef.current) {
      try {
        const state = html5QrCodeRef.current.getState?.();
        if (state === 2 || html5QrCodeRef.current.isScanning) { // 2 = SCANNING state
          await html5QrCodeRef.current.stop();
        }
      } catch (e) { 
        console.log('Stop scanner warning:', e);
      }
      try {
        html5QrCodeRef.current.clear();
      } catch (e) {
        console.log('Clear scanner warning:', e);
      }
      html5QrCodeRef.current = null;
    }
    setScanning(false);
    setCameraLoading(false);
  };

  // Handle manual QR entry
  const handleManualQrSubmit = async () => {
    if (!manualQrInput.trim()) {
      toast.error('Please enter a QR code or delegate ID');
      return;
    }
    await handleScanResult(manualQrInput.trim());
    setManualQrInput('');
  };

  // Retry camera initialization
  const retryCamera = async () => {
    setCameraError(null);
    setCameraList([]);
    setSelectedCamera(null);
    await initializeCameras();
  };

  const handleScanResult = useCallback(async (decodedText) => {
    // Pause scanner
    if (html5QrCodeRef.current) {
      try { await html5QrCodeRef.current.pause(); } catch (e) { console.warn("QR cleanup:", e?.message); }
    }

    // ─────────────────────────────────────────────────────────────
    // Iter-438c · Kiosk scan diagnostics
    //   Build a specific, meaningful result for every failure path
    //   instead of the previous catch-all "Connection error" message.
    // ─────────────────────────────────────────────────────────────
    const classifyResult = ({ res, data, thrown, parseError, timedOut }) => {
      // 1. True offline detection (browser API)
      if (typeof navigator !== 'undefined' && navigator && navigator.onLine === false) {
        return { success: false, allowed: false, status: 'offline',
                  message: 'No internet connection',
                  reason: 'Your device is offline. Reconnect and try again.',
                  color: 'red', error_code: 'KIOSK_OFFLINE' };
      }

      // 2. fetch() threw before any response (real network / DNS / CORS)
      if (thrown && !res) {
        const name = thrown.name || '';
        const msg = String(thrown.message || thrown || '');
        if (timedOut) {
          return { success: false, allowed: false, status: 'timeout',
                    message: 'Server took too long to respond',
                    reason: 'The scan request timed out after 15s. Please retry.',
                    color: 'red', error_code: 'KIOSK_TIMEOUT' };
        }
        // TypeError: "Failed to fetch" == network layer failure (DNS/CORS/SSL/offline)
        return { success: false, allowed: false, status: 'network_error',
                  message: 'Unable to reach the server',
                  reason: `Network request failed (${name || 'network'}: ${msg.slice(0, 120)}). Check the kiosk network or ingress.`,
                  color: 'red', error_code: 'KIOSK_NETWORK' };
      }

      // 3. Response arrived but body wasn't valid JSON
      if (parseError && res) {
        return { success: false, allowed: false, status: 'bad_response',
                  message: 'Server returned an unexpected response',
                  reason: `HTTP ${res.status} but the body was not JSON. This usually indicates a reverse-proxy error page (502/503/504) or misrouted request.`,
                  color: 'red', error_code: `KIOSK_HTTP_${res.status}` };
      }

      // 4. Response arrived, JSON parsed — but HTTP status is not 2xx
      if (res && !res.ok) {
        const code = res.status;
        // Prefer the backend's own message/reason if it supplied one.
        const backendMsg = (data && (data.message || data.detail)) || '';
        const backendReason = (data && data.reason) || '';
        if (code === 401) {
          return { success: false, allowed: false, status: 'auth_expired',
                    message: 'Session expired',
                    reason: backendReason || backendMsg || 'Kiosk auth token is invalid or expired. Sign in again to continue scanning.',
                    color: 'red', error_code: 'KIOSK_401' };
        }
        if (code === 403) {
          return { success: false, allowed: false, status: 'forbidden',
                    message: 'Not authorised',
                    reason: backendReason || backendMsg || 'This account is not permitted to scan for this event.',
                    color: 'red', error_code: 'KIOSK_403' };
        }
        if (code === 404) {
          return { success: false, allowed: false, status: 'not_found',
                    message: 'Event or endpoint not found',
                    reason: backendReason || backendMsg || 'The kiosk scan endpoint returned 404. Verify the event id and that the kiosk build is up to date.',
                    color: 'red', error_code: 'KIOSK_404' };
        }
        if (code === 422) {
          return { success: false, allowed: false, status: 'bad_request',
                    message: 'Invalid scan payload',
                    reason: backendReason || backendMsg || 'The scanner sent a malformed request. Try re-scanning; if it persists, contact support.',
                    color: 'red', error_code: 'KIOSK_422' };
        }
        if (code === 429) {
          return { success: false, allowed: false, status: 'rate_limited',
                    message: 'Slow down — too many scans',
                    reason: backendReason || backendMsg || 'You are being rate-limited by the server. Wait a moment and rescan.',
                    color: 'red', error_code: 'KIOSK_429' };
        }
        if (code >= 500) {
          return { success: false, allowed: false, status: 'server_error',
                    message: 'Server error',
                    reason: backendReason || backendMsg || `The server returned ${code}. Please retry; if it continues, contact support.`,
                    color: 'red', error_code: `KIOSK_${code}` };
        }
        // Any other unexpected non-2xx
        return { success: false, allowed: false, status: 'http_error',
                  message: `Unexpected server response (${code})`,
                  reason: backendReason || backendMsg || 'The server returned an unexpected status. Please retry.',
                  color: 'red', error_code: `KIOSK_${code}` };
      }

      // 5. HTTP 2xx — trust the backend payload.
      return data;
    };

    let res = null;
    let data = null;
    let thrown = null;
    let parseError = false;
    let timedOut = false;

    // 15-second timeout to avoid an indefinite "scanning" state when the
    // backend or ingress is unresponsive.
    const controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => { timedOut = true; controller.abort(); }, 15000) : null;

    try {
      // Build request body with mode-specific params
      const body = { qr_data: decodedText, scan_mode: scanMode.id };
      if (scanMode.id === 'meal') {
        body.meal_type = mealType;
        body.dining_zone = diningZone;
      } else if (scanMode.id === 'accommodation') {
        body.building = building || null;
        body.access_point = accessPoint;
      } else if (scanMode.id === 'session') {
        body.session_name = sessionName || null;
      } else if (scanMode.id === 'security' || scanMode.id === 'vip') {
        body.access_zone = accessZone;
      }

      res = await fetch(`${API_URL}/api/events/${eventId}/kiosk/scan`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
        signal: controller ? controller.signal : undefined,
      });

      try {
        data = await res.json();
      } catch (jsonErr) {
        parseError = true;
        console.warn('[kiosk] JSON parse failed:', jsonErr?.message, 'status=', res.status);
      }
    } catch (fetchErr) {
      thrown = fetchErr;
      console.warn('[kiosk] fetch threw:', fetchErr?.name, fetchErr?.message);
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }

    const finalData = classifyResult({ res, data, thrown, parseError, timedOut });

    if (finalData && finalData.allowed) {
      playSound(true);
      setStats(prev => ({ ...prev, scanned: prev.scanned + 1, approved: prev.approved + 1 }));
    } else {
      playSound(false);
      setStats(prev => ({ ...prev, scanned: prev.scanned + 1, denied: prev.denied + 1 }));
    }

    setResult(finalData);

    setRecentScans(prev => [{
      name: finalData?.delegate?.name || (finalData?.error_code ? `[${finalData.error_code}]` : 'Unknown'),
      status: finalData?.status || 'unknown',
      time: new Date().toLocaleTimeString(),
      allowed: !!finalData?.allowed,
    }, ...prev].slice(0, 10));

    // Auto-resume after 5 seconds
    setTimeout(() => {
      setResult(null);
      if (html5QrCodeRef.current && scanning) {
        try { html5QrCodeRef.current.resume(); } catch (e) { console.warn("QR cleanup:", e?.message); }
      }
    }, 5000);
  }, [eventId, scanMode, mealType, diningZone, building, accessPoint, sessionName, accessZone, scanning, playSound]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(console.log);
    } else {
      document.exitFullscreen().catch(console.log);
    }
  };

  const handleModeSelect = (mode) => {
    setScanMode(SCAN_MODES[mode]);
  };

  const handleBack = () => {
    if (scanning) {
      stopScanner();
    }
    if (scanMode) {
      setScanMode(null);
    } else {
      navigate(-1);
    }
  };

  // Get result display color
  const getResultColor = (data) => {
    if (!data) return 'bg-slate-700';
    if (data.allowed) return 'bg-green-500';
    if (data.status === 'already_checked_in' || data.status === 'already_redeemed') return 'bg-yellow-500';
    if (data.status === 'offline' || data.status === 'timeout' || data.status === 'network_error' ||
        data.status === 'bad_response' || data.status === 'server_error' || data.status === 'rate_limited') {
      // Infrastructure failures — orange, not red, to distinguish from delegate-level denies.
      return 'bg-orange-500';
    }
    return 'bg-red-500';
  };

  const getResultIcon = (data) => {
    if (!data) return XCircle;
    if (data.allowed) return CheckCircle;
    if (data.status === 'already_checked_in' || data.status === 'already_redeemed') return AlertTriangle;
    if (data.status === 'offline' || data.status === 'timeout' || data.status === 'network_error' ||
        data.status === 'bad_response' || data.status === 'server_error' || data.status === 'rate_limited') {
      return AlertTriangle;
    }
    return XCircle;
  };

  // Mode Selection Screen
  if (!scanMode) {
    return (
      <div className="min-h-screen bg-gray-900 text-white" data-testid="unified-kiosk">
        {/* Header */}
        <div className="bg-gray-800 p-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">{event?.name || 'Event Kiosk'}</h1>
            <p className="text-gray-400 text-sm">Select scanning mode</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="text-white border-gray-600 hover:bg-gray-700"
            >
              {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleFullscreen}
              className="text-white border-gray-600 hover:bg-gray-700"
            >
              <Maximize className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(-1)}
              className="text-white border-gray-600 hover:bg-gray-700"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Mode Selection */}
        <div className="p-6 max-w-4xl mx-auto">
          <h2 className="text-xl font-semibold text-center mb-6">What are you scanning for?</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.values(SCAN_MODES).map((mode) => {
              const Icon = mode.icon;
              return (
                <Card 
                  key={mode.id}
                  className={`cursor-pointer transition-all hover:scale-[1.02] bg-gradient-to-r ${mode.color} border-0`}
                  onClick={() => handleModeSelect(mode.id)}
                  data-testid={`mode-${mode.id}`}
                >
                  <CardContent className="p-6 flex flex-col items-center text-center">
                    <div className="bg-white/20 rounded-xl p-4 mb-4">
                      <Icon className="h-10 w-10 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{mode.name}</h3>
                    <p className="text-white/70 text-sm">{mode.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Stats Summary */}
          <Card className="mt-8 bg-gray-800 border-gray-700">
            <CardContent className="p-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-3xl font-bold text-white">{stats.scanned}</div>
                  <div className="text-sm text-gray-400">Total Scans</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-green-400">{stats.approved}</div>
                  <div className="text-sm text-gray-400">Approved</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-red-400">{stats.denied}</div>
                  <div className="text-sm text-gray-400">Denied</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Scanner Screen
  const ModeIcon = scanMode.icon;

  return (
    <div className="min-h-screen bg-gray-900 text-white" data-testid={`kiosk-${scanMode.id}`}>
      {/* Header */}
      <div className={`${scanMode.bgColor} p-4 flex justify-between items-center`}>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBack}
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <ModeIcon className="h-5 w-5" />
              {scanMode.name}
            </h1>
            <p className="text-white/70 text-sm">{event?.name}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="text-white hover:bg-white/20"
          >
            {soundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleFullscreen}
            className="text-white hover:bg-white/20 hidden md:flex"
          >
            <Maximize className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row h-[calc(100vh-80px)]">
        {/* Scanner Area */}
        <div className="flex-1 flex flex-col items-center justify-center p-4">
          {/* Mode-specific settings */}
          <div className="mb-4 flex gap-4 flex-wrap justify-center">
            {scanMode.id === 'meal' && (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">Meal:</span>
                  <Select value={mealType} onValueChange={setMealType}>
                    <SelectTrigger className="w-32 bg-gray-800 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="breakfast">Breakfast</SelectItem>
                      <SelectItem value="lunch">Lunch</SelectItem>
                      <SelectItem value="dinner">Dinner</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">Zone:</span>
                  <Select value={diningZone} onValueChange={setDiningZone}>
                    <SelectTrigger className="w-32 bg-gray-800 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="main">Main Hall</SelectItem>
                      <SelectItem value="vip">VIP Dining</SelectItem>
                      <SelectItem value="staff">Staff Area</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {scanMode.id === 'accommodation' && (
              <>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">Building:</span>
                  <Input
                    value={building}
                    onChange={(e) => setBuilding(e.target.value)}
                    placeholder="Any building"
                    className="w-40 bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-400">Entry:</span>
                  <Select value={accessPoint} onValueChange={setAccessPoint}>
                    <SelectTrigger className="w-32 bg-gray-800 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="main">Main Entrance</SelectItem>
                      <SelectItem value="side">Side Entrance</SelectItem>
                      <SelectItem value="parking">Parking Access</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            {scanMode.id === 'session' && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">Session:</span>
                <Input
                  value={sessionName}
                  onChange={(e) => setSessionName(e.target.value)}
                  placeholder="Session name (optional)"
                  className="w-48 bg-gray-800 border-gray-600 text-white"
                />
              </div>
            )}
            {(scanMode.id === 'security' || scanMode.id === 'vip') && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-400">Zone:</span>
                <Select value={accessZone} onValueChange={setAccessZone}>
                  <SelectTrigger className="w-36 bg-gray-800 border-gray-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="main">Main Area</SelectItem>
                    <SelectItem value="vip">VIP Lounge</SelectItem>
                    <SelectItem value="backstage">Backstage</SelectItem>
                    <SelectItem value="restricted">Restricted</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {!scanning ? (
            <div className="text-center">
              {/* Camera Error Display */}
              {cameraError && (
                <div className="mb-6 p-4 bg-red-900/50 border border-red-700 rounded-lg text-red-200 max-w-md mx-auto">
                  <div className="flex items-center gap-2 mb-2">
                    <XCircle className="h-5 w-5" />
                    <span className="font-semibold">Camera Error</span>
                  </div>
                  <p className="text-sm">{cameraError}</p>
                  <div className="mt-3 flex gap-2 justify-center">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={retryCamera}
                      className="text-white border-red-600 hover:bg-red-800"
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Retry Camera
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowManualEntry(true)}
                      className="text-white border-gray-600 hover:bg-gray-700"
                    >
                      Manual Entry
                    </Button>
                  </div>
                </div>
              )}

              {/* Camera Selector */}
              {cameraList.length > 1 && !cameraError && (
                <div className="mb-4 flex items-center justify-center gap-2">
                  <span className="text-sm text-gray-400">Camera:</span>
                  <Select value={selectedCamera} onValueChange={setSelectedCamera}>
                    <SelectTrigger className="w-64 bg-gray-800 border-gray-600 text-white">
                      <SelectValue placeholder="Select camera" />
                    </SelectTrigger>
                    <SelectContent>
                      {cameraList.map(cam => (
                        <SelectItem key={cam.id} value={cam.id}>
                          {cam.label || `Camera ${cameraList.indexOf(cam) + 1}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="relative inline-block mb-8">
                <div className={`w-32 h-32 bg-gradient-to-br ${scanMode.color} rounded-2xl flex items-center justify-center`}>
                  <ModeIcon className="h-16 w-16 text-white" />
                </div>
              </div>
              <h2 className="text-3xl font-bold mb-4">Ready to Scan</h2>
              <p className="text-gray-400 mb-8 max-w-sm mx-auto">
                Scan delegate badge to verify {scanMode.name.toLowerCase()}
              </p>
              
              <div className="flex flex-col items-center gap-4">
                <Button 
                  onClick={startScanner} 
                  size="lg"
                  disabled={cameraLoading || (!selectedCamera && cameraList.length === 0 && !cameraError)}
                  className={`text-xl px-8 py-6 bg-gradient-to-r ${scanMode.color} disabled:opacity-50`}
                  data-testid="start-scanner-btn"
                >
                  {cameraLoading ? (
                    <>
                      <RefreshCw className="h-6 w-6 mr-2 animate-spin" />
                      Starting Camera...
                    </>
                  ) : (
                    <>
                      <Camera className="h-6 w-6 mr-2" />
                      Start Scanner
                    </>
                  )}
                </Button>

                {/* Manual Entry Toggle */}
                <Button 
                  variant="ghost" 
                  onClick={() => setShowManualEntry(!showManualEntry)}
                  className="text-gray-400 hover:text-white"
                >
                  {showManualEntry ? 'Hide' : 'Show'} Manual Entry
                </Button>

                {/* Manual QR Entry */}
                {showManualEntry && (
                  <div className="w-full max-w-md p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                    <p className="text-sm text-gray-400 mb-3">Enter QR code data or delegate ID manually:</p>
                    <div className="flex gap-2">
                      <Input
                        value={manualQrInput}
                        onChange={(e) => setManualQrInput(e.target.value)}
                        placeholder="Delegate ID, Badge Code, or QR data..."
                        className="bg-gray-800 border-gray-600 text-white"
                        onKeyDown={(e) => e.key === 'Enter' && handleManualQrSubmit()}
                        data-testid="manual-qr-input"
                      />
                      <Button 
                        onClick={handleManualQrSubmit}
                        className={`bg-gradient-to-r ${scanMode.color}`}
                        data-testid="manual-qr-verify-btn"
                      >
                        Verify
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="relative w-full max-w-lg">
              <div className="relative bg-black rounded-2xl overflow-hidden shadow-2xl">
                <div id={scannerContainerId} className="w-full aspect-square" />
                
                {/* Scanning overlay animation */}
                {!result && (
                  <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute inset-0 border-4 border-white/20 m-8 rounded-lg" />
                    <div className="absolute left-8 right-8 top-1/3 h-0.5 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-pulse" />
                  </div>
                )}
                
                {/* Result Overlay */}
                {result && (
                  <div 
                    className={`absolute inset-0 flex items-center justify-center ${getResultColor(result)}`}
                    data-testid="scan-result"
                  >
                    <div className="text-center p-6 max-w-md w-full">
                      {/* Icon */}
                      <div className="mb-4">
                        {React.createElement(getResultIcon(result), { className: "h-20 w-20 mx-auto text-white" })}
                      </div>
                      
                      {/* Main Message */}
                      <div className="text-3xl font-bold mb-2 text-white">
                        {result.message}
                      </div>
                      
                      {/* Reason */}
                      <div className="text-lg text-white/90 mb-4">
                        {result.reason}
                      </div>

                      {/* Support error code — surfaced only when the scan
                          failed for infrastructure reasons so operators
                          can quote it to support. */}
                      {result.error_code && (
                        <div className="text-xs text-white/70 font-mono mb-4"
                             data-testid="scan-result-error-code">
                          Error code: {result.error_code}
                        </div>
                      )}
                      
                      {/* Delegate Info */}
                      {result.delegate && (
                        <div className="bg-white/10 rounded-lg p-4 space-y-2 text-left">
                          <div className="flex items-center gap-2 text-white">
                            <User className="h-5 w-5" />
                            <span className="text-xl font-semibold">{result.delegate.name}</span>
                          </div>
                          
                          {result.delegate.company && (
                            <div className="flex items-center gap-2 text-white/80 text-sm">
                              <Building className="h-4 w-4" />
                              {result.delegate.company}
                              {result.delegate.title && <span>• {result.delegate.title}</span>}
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2 text-white/70 text-sm">
                            <Ticket className="h-4 w-4" />
                            <span>Reg#: {result.delegate.registration_number}</span>
                          </div>
                          
                          {result.delegate.tier && (
                            <Badge className="bg-white/20 text-white">
                              {result.delegate.tier}
                            </Badge>
                          )}
                          
                          {/* Mode-specific details */}
                          {result.meal && (
                            <div className="mt-3 pt-3 border-t border-white/20">
                              <div className="text-sm text-white/80">
                                <Utensils className="h-4 w-4 inline mr-1" />
                                {result.meal.type?.charAt(0).toUpperCase() + result.meal.type?.slice(1)} - {result.meal.zone}
                              </div>
                            </div>
                          )}
                          
                          {result.accommodation && (
                            <div className="mt-3 pt-3 border-t border-white/20">
                              <div className="text-sm text-white/80">
                                <Home className="h-4 w-4 inline mr-1" />
                                {result.accommodation.details}
                              </div>
                              {result.accommodation.room_number && result.accommodation.room_number !== 'N/A' && (
                                <div className="text-sm text-white/70">
                                  Room: {result.accommodation.room_number}
                                </div>
                              )}
                            </div>
                          )}
                          
                          {result.session && (
                            <div className="mt-3 pt-3 border-t border-white/20">
                              <div className="text-sm text-white/80">
                                <BookOpen className="h-4 w-4 inline mr-1" />
                                {result.session.title}
                              </div>
                              <div className="text-sm text-white/70">
                                <MapPin className="h-4 w-4 inline mr-1" />
                                {result.session.room}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Scanner controls */}
              <div className="mt-4 flex flex-col items-center gap-3">
                {/* Camera Error while scanning */}
                {cameraError && (
                  <div className="w-full p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-200 text-sm">
                    <div className="flex items-center gap-2">
                      <XCircle className="h-4 w-4" />
                      <span>{cameraError}</span>
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  {cameraList.length > 1 && (
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        const currentIndex = cameraList.findIndex(c => c.id === selectedCamera);
                        const nextIndex = (currentIndex + 1) % cameraList.length;
                        setSelectedCamera(cameraList[nextIndex].id);
                        if (scanning) {
                          stopScanner();
                          setTimeout(startScanner, 100);
                        }
                      }}
                      className="text-white border-gray-600 hover:bg-gray-700"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Switch Camera
                    </Button>
                  )}
                  <Button 
                    variant="outline" 
                    onClick={stopScanner} 
                    className="text-white border-gray-600 hover:bg-gray-700"
                    data-testid="stop-scanner-btn"
                  >
                    <CameraOff className="h-4 w-4 mr-2" />
                    Stop Scanner
                  </Button>
                </div>

                {/* Manual entry while scanning */}
                <div className="w-full max-w-md">
                  <div className="flex gap-2">
                    <Input
                      value={manualQrInput}
                      onChange={(e) => setManualQrInput(e.target.value)}
                      placeholder="Or enter QR/ID manually..."
                      className="bg-gray-800 border-gray-600 text-white text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && handleManualQrSubmit()}
                      data-testid="manual-qr-input-inline"
                    />
                    <Button 
                      onClick={handleManualQrSubmit}
                      size="sm"
                      className={`bg-gradient-to-r ${scanMode.color}`}
                      data-testid="manual-qr-verify-inline-btn"
                    >
                      Verify
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="w-full lg:w-80 bg-gray-800 p-4 overflow-y-auto">
          {/* Current Mode */}
          <Card className={`${scanMode.bgColor}/20 border-${scanMode.bgColor}/30 mb-4`}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-2 mb-2">
                <ModeIcon className="h-5 w-5 text-white" />
                <span className="text-white font-medium">{scanMode.name}</span>
              </div>
              {scanMode.id === 'meal' && (
                <div className="text-sm text-gray-400 capitalize">
                  {mealType} • {diningZone} Dining
                </div>
              )}
              {scanMode.id === 'accommodation' && building && (
                <div className="text-sm text-gray-400">
                  {building} • {accessPoint} entrance
                </div>
              )}
            </CardContent>
          </Card>

          {/* Session Stats */}
          <Card className="bg-gray-700/50 border-gray-600 mb-4">
            <CardContent className="pt-4">
              <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Session Stats
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-xl font-bold text-white">{stats.scanned}</div>
                  <div className="text-xs text-gray-400">Scanned</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-green-400">{stats.approved}</div>
                  <div className="text-xs text-gray-400">Approved</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-red-400">{stats.denied}</div>
                  <div className="text-xs text-gray-400">Denied</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recent Scans */}
          <h3 className="font-semibold text-white mb-3">Recent Scans</h3>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {recentScans.map((scan, i) => (
              <div 
                key={scan.timestamp || `scan-${i}`}
                className={`p-2 rounded-lg text-sm ${
                  scan.allowed 
                    ? 'bg-green-900/30 border border-green-700/30' 
                    : 'bg-red-900/30 border border-red-700/30'
                }`}
              >
                <div className="flex justify-between items-center">
                  <span className="font-medium text-white">{scan.name}</span>
                  {scan.allowed ? (
                    <CheckCircle className="h-4 w-4 text-green-400" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-400" />
                  )}
                </div>
                <div className="flex justify-between text-gray-400 text-xs mt-1">
                  <span className="capitalize">{scan.status?.replace(/_/g, ' ')}</span>
                  <span>{scan.time}</span>
                </div>
              </div>
            ))}
            {recentScans.length === 0 && (
              <p className="text-gray-500 text-sm text-center py-4">No scans yet</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedKiosk;
